import {
  findParentUserMessage,
  isAutoModeAttribution,
  resolveAttributionModelIds,
} from '@/constants/utils/message';

function formatDate(timestamp) {
  if (!timestamp) return '';
  const d = new Date(timestamp);
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getRoleName(message, getModelById, parentUserMessage) {
  if (message.role === 'user') return 'You';
  if (message.role === 'assistant') {
    // Same identity as the on-screen card: a manual Arya pick stays Arya
    // even when political/intent routing ran Gemini (or any other model).
    const isAuto = isAutoModeAttribution(parentUserMessage, message);
    const ids = resolveAttributionModelIds(message, parentUserMessage, isAuto);
    if (ids.length > 0 && getModelById) {
      const names = ids.map((id) => getModelById(id)?.name).filter(Boolean);
      if (names.length > 0) return names.join(', ');
    }
    return 'Assistant';
  }
  return message.role || 'Unknown';
}

function getVisibleMessages(messages) {
  if (!Array.isArray(messages)) return [];
  return messages.filter(
    (m) => m.role === 'user' || m.role === 'assistant'
  ).filter(
    (m) => !m.flags?.isDeleted && !m.flags?.isHidden
  ).filter(
    (m) => m.content || (Array.isArray(m.images) && m.images.length > 0)
  );
}

function getImagePlaceholder(images) {
  if (!Array.isArray(images) || images.length === 0) return '';
  const count = images.length;
  const lines = [];
  for (let i = 0; i < count; i++) {
    const img = images[i];
    const prompt = img?.prompt;
    if (prompt) {
      lines.push(`[Image ${count > 1 ? `${i + 1}/${count}` : ''}: ${prompt}]`.replace(' :', ':'));
    } else {
      lines.push(`[Image${count > 1 ? ` ${i + 1}/${count}` : ''}]`);
    }
  }
  return lines.join('\n');
}

/**
 * Convert a conversation to Markdown
 */
export function toMarkdown(chat, { getModelById } = {}) {
  const title = chat.title || 'Untitled Conversation';
  const createdAt = formatDate(chat.createdAt);
  const allMessages = Array.isArray(chat.messages) ? chat.messages : [];
  const messages = getVisibleMessages(allMessages);

  const lines = [];
  lines.push(`# ${title}`);
  if (createdAt) lines.push(`*${createdAt}*`);
  lines.push('');
  lines.push('---');
  lines.push('');

  for (const msg of messages) {
    const parent = msg.role === 'assistant' ? findParentUserMessage(allMessages, msg) : null;
    const role = getRoleName(msg, getModelById, parent);
    const time = formatDate(msg.timestamp);
    lines.push(`### ${role}`);
    if (time) lines.push(`*${time}*`);
    lines.push('');

    const imagePlaceholder = getImagePlaceholder(msg.images);
    if (imagePlaceholder) {
      lines.push(imagePlaceholder);
      lines.push('');
    }

    if (msg.content) {
      lines.push(msg.content);
      lines.push('');
    }

    lines.push('---');
    lines.push('');
  }

  lines.push(`*Exported from Gab AI on ${formatDate(new Date())}*`);

  return lines.join('\n');
}

/**
 * Convert a conversation to plain text
 */
export function toPlainText(chat, { getModelById } = {}) {
  const title = chat.title || 'Untitled Conversation';
  const createdAt = formatDate(chat.createdAt);
  const allMessages = Array.isArray(chat.messages) ? chat.messages : [];
  const messages = getVisibleMessages(allMessages);

  const lines = [];
  lines.push(title);
  if (createdAt) lines.push(createdAt);
  lines.push('='.repeat(Math.min(title.length, 60)));
  lines.push('');

  for (const msg of messages) {
    const parent = msg.role === 'assistant' ? findParentUserMessage(allMessages, msg) : null;
    const role = getRoleName(msg, getModelById, parent);
    const time = formatDate(msg.timestamp);
    lines.push(`[${role}]${time ? ` (${time})` : ''}`);

    const imagePlaceholder = getImagePlaceholder(msg.images);
    if (imagePlaceholder) {
      lines.push(imagePlaceholder);
    }

    if (msg.content) {
      lines.push(msg.content);
    }

    lines.push('');
    lines.push('-'.repeat(40));
    lines.push('');
  }

  lines.push(`Exported from Gab AI on ${formatDate(new Date())}`);

  return lines.join('\n');
}
